import { useDropzone } from 'react-dropzone'
import { DECK_MAX_BYTES, deckRejectionMessage, useDeckUpload } from '../../../hooks/use-deck-upload'

/**
 * One deck-upload target, driven from two places.
 *
 * The Home card shows a drop zone *and* an "Upload your deck" button, and they
 * were doing different things: the zone opened a file picker and uploaded in
 * place, the button navigated to the profile page. Two ways to answer the same
 * ask is two ways to get a different result, and the button was the slower one.
 *
 * Returning `open` alongside the drop props lets the card render the zone and
 * hand the button the same trigger, so both end at the same picker and the same
 * upload.
 */
export function useDeckDrop(startupId: string, onUploaded?: () => void) {
  const { upload, isUploading, error, setError } = useDeckUpload(startupId)

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    accept: { 'application/pdf': ['.pdf'] },
    maxFiles: 1,
    maxSize: DECK_MAX_BYTES,
    disabled: isUploading,
    onDropAccepted: async (files) => {
      const file = files[0]
      if (!file) return
      const succeeded = await upload(file)
      // Home re-resolves to the reading-deck state once the link lands, so the
      // card replaces itself rather than reporting success in place. Only on
      // an actual success — `upload` used to swallow its own errors and
      // resolve either way, which fired this even after a failed upload.
      if (succeeded) onUploaded?.()
    },
    onDropRejected: (rejections) =>
      setError(
        deckRejectionMessage(rejections[0]?.errors[0]?.code, rejections[0]?.errors[0]?.message)
      ),
  })

  return { getRootProps, getInputProps, isDragActive, open, isUploading, error }
}
